/**
 * RS Customizer — Path Bead
 * SVG path-based bead renderer for path bead bracelet customization.
 * Letter bead images are fetched via GraphQL; regular/spacer beads come from
 * the metaobject. Delegates all rendering to RSBeadPath.renderBeadsInContainer
 * which handles letter centering, spacer placement, and blank bead fill.
 * Requires window.RSBeadPath (rs-bead-path-utils.js).
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is
 * managed by rs-product-core.js. This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const METAOBJECT_TYPE = 'customizer_path_bead_set';
  const LIQUID_VIEW = 'metaobject-path-bead';
  const PATH_BEAD_SHADOW = { distance: 6, opacity: 0.25, blur: 1.5 };

  let _injectedSlideId = null;
  let _baseConfig = null;
  let _letters = {};
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _beadConfig = null;
  let _resizeTimer = null;

  const CONFIG = {
    svgContainerClass: 'bead-path-svg-container',
    beadContainerClass: 'bead-path-bead-container',
    letterBeadClass: 'bead-path-letter-bead',
    spacerBeadClass: 'bead-path-spacer-bead',
    blankBeadClass: 'bead-path-blank-bead',
  };

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) { text += field.value; });
    return text.trim();
  }

  function _normalizeTitle(title) {
    return (title || '').replace(/['''']/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // ─── Data fetching ──────────────────────────────────────────────

  function _fetchBaseConfig() {
    return fetch(window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW)
      .then(function (response) { return response.json(); });
  }

  function _fetchLetters() {
    if (!window.RSGraphQL || !window.RSGraphQL.fetchCustomizerLetters) {
      return Promise.resolve({});
    }
    return window.RSGraphQL.fetchCustomizerLetters(METAOBJECT_TYPE, {})
      .then(function (raw) {
        if (!Array.isArray(raw)) return {};
        const normalizedProductTitle = _normalizeTitle(
          (_baseConfig && _baseConfig.product_title) || ''
        );
        const result = {};
        raw.forEach(function (letterEntry) {
          if (!letterEntry || !letterEntry.key || !letterEntry.image) return;
          if (
            normalizedProductTitle &&
            !_normalizeTitle(letterEntry.name || '').includes(normalizedProductTitle)
          ) return;
          const key = window.RSBeadPath.fixDoubleEscapedUnicode(letterEntry.key);
          result[key] = {
            name: letterEntry.name || '',
            url: letterEntry.image.url,
            width: letterEntry.image.width || 150,
            height: letterEntry.image.height || 150,
            aspectRatio: (letterEntry.image.width && letterEntry.image.height)
              ? letterEntry.image.width / letterEntry.image.height
              : 1,
            left_recess: letterEntry.left_recess || 0,
            right_recess: letterEntry.right_recess || 0,
          };
        });
        return result;
      });
  }

  // ─── Background management ──────────────────────────────────────

  function _getBackgroundUrl() {
    if (!_backgroundsData) return null;
    if (_backgroundsData['default']) return _backgroundsData['default'];
    const key = Object.keys(_backgroundsData).find(function (k) { return _backgroundsData[k]; });
    return key ? _backgroundsData[key] : null;
  }

  function _getThumbnailUrl() {
    const thumbs = (_baseConfig && _baseConfig.thumbnails) || {};
    if (thumbs['default']) return thumbs['default'];
    const key = Object.keys(thumbs).find(function (k) { return thumbs[k]; });
    return key ? thumbs[key] : null;
  }

  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return _getBackgroundUrl();
    const normalizedTitle = variantTitle.toLowerCase();
    let match = null;
    Object.entries(_backgroundsData).forEach(function (pair) {
      if (
        pair[0].toLowerCase() === normalizedTitle ||
        normalizedTitle.includes(pair[0].toLowerCase())
      ) {
        match = pair[1];
      }
    });
    return match || _getBackgroundUrl();
  }

  // Override the gallery thumbnail image with the dedicated thumbnail URL from the metaobject.
  // gallery.inject creates a thumbnail from the main background image; the metaobject provides
  // a separate smaller thumbnail image specifically designed for the gallery strip.
  function _applyThumbnailImage() {
    const thumbUrl = _getThumbnailUrl();
    if (!thumbUrl || !_injectedSlideId) return;
    const allThumbs = document.querySelectorAll('.product__thumb img, .splide__slide--thumbnail img');
    const bgUrl = _getBackgroundUrl();
    if (bgUrl) {
      allThumbs.forEach(function (img) {
        if (img.src && img.src.indexOf(bgUrl.split('?')[0]) !== -1) {
          img.src = thumbUrl;
        }
      });
    }
  }

  // ─── Bead config ────────────────────────────────────────────────

  function _buildBeadConfig() {
    if (!_baseConfig || !_baseConfig.settings) return null;
    const settings = _baseConfig.settings;
    return {
      svg_guide_content: settings.svg_guide_content,
      svg_view_box_width: settings.svg_view_box_width || 1110,
      svg_view_box_height: settings.svg_view_box_height || 880,
      bead_height: settings.bead_height || 90,
      letter_height: settings.letter_height || 90,
      bead_width: settings.bead_width || null,
      letter_width: settings.letter_width || null,
      spacer_width: settings.spacer_width || null,
      spacer_height: settings.spacer_height || null,
      letter_spacing: settings.letter_spacing || 0,
      bead_spacing: settings.bead_spacing || 0,
      y_offset: settings.y_offset || 0,
      random_offset: settings.random_offset === true || settings.random_offset === 'true',
      offset_factor: settings.offset_factor || 0,
      light_source_angle: settings.light_source_angle || 44,
      stroke_width: settings.stroke_width || 0,
      stroke_color: settings.stroke_color || 'rgba(0,0,0,0)',
      beads: settings.beads || [],
      spacer: settings.spacer || {},
      shadow: PATH_BEAD_SHADOW,
    };
  }

  // ─── Canvas helpers ─────────────────────────────────────────────

  function _getCustomizerView() {
    if (!_injectedSlideId) return null;
    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;
    return slide.querySelector('.customizer-view');
  }

  // ─── Render helper ──────────────────────────────────────────────

  function _render() {
    const container = _getCustomizerView();
    if (!container || !_beadConfig) return;
    window.RSBeadPath.renderBeadsInContainer(
      container, _getMtoText(), _beadConfig, _letters, CONFIG
    );
  }

  // ─── Icon mapping helpers ────────────────────────────────────────

  function _decodeUtfChar(str) {
    return str.replace(/\\u([0-9a-fA-F]{4})/g, function (_, hex) {
      return String.fromCharCode(parseInt(hex, 16));
    });
  }

  function _handleIconButtonClick(event) {
    const btn = event.target.closest('[data-utf-char]');
    if (!btn || !Object.keys(_letters).length) return;

    const sectionEl = document.getElementById('MainProduct--' + window.RSProduct.sectionId);
    if (!sectionEl || !sectionEl.contains(btn)) return;

    const title = (btn.dataset.title || '').trim();
    const rawUtf = btn.dataset.utfChar || '';
    if (!title || !rawUtf) return;

    const decoded = _decodeUtfChar(rawUtf);
    if (!decoded) return;

    const keys = Object.keys(_letters);
    const titleLower = title.toLowerCase();
    let match = keys.find(function (k) {
      return k.toLowerCase() === titleLower;
    });
    if (!match) {
      match = keys.find(function (k) {
        const name = (_letters[k].name || '').toLowerCase();
        return name === titleLower || name.endsWith(' ' + titleLower);
      });
    }
    if (!match && _letters[decoded]) {
      match = decoded;
    }

    if (match) {
      window.RS = window.RS || {};
      window.RS.iconMapping = window.RS.iconMapping || {};
      window.RS.iconMapping[decoded] = match;
    }
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _render();
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;
    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const bgUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (bgUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(bgUrl, 1);
      setTimeout(_applyThumbnailImage, 300);
    }

    _render();

    if (_injectedSlideId && _getMtoText()) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () { _render(); }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  function _init() {
    if (!window.RSProduct || !window.RSProduct.config.productHandle) return;
    if (!window.RSBeadPath) {
      console.error('[PathBead] RSBeadPath not loaded — rs-bead-path-utils.js must load first');
      return;
    }

    _fetchBaseConfig()
      .then(function (config) {
        _baseConfig = config;
        _backgroundsData = config.backgrounds || {};

        if (config.settings) {
          document.documentElement.style.setProperty(
            '--customizer-path-stroke-width',
            (config.settings.stroke_width || 0) + 'px'
          );
          document.documentElement.style.setProperty(
            '--customizer-path-stroke-color',
            config.settings.stroke_color || 'rgba(0,0,0,0)'
          );
        }

        const initVarId = String(window.RSProduct.config.initialVariantId || '');
        const variantNames = config.variant_names || {};
        const fullName = variantNames[initVarId] || '';
        const parts = fullName.split(' - ');
        _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

        const bgUrl = _getBackgroundForVariant(_currentVariantTitle);
        if (bgUrl) {
          _injectedSlideId = window.RSProduct.gallery.inject(bgUrl, 1);
          setTimeout(_applyThumbnailImage, 300);
        }

        return _fetchLetters();
      })
      .then(function (letters) {
        _letters = letters || {};
        _beadConfig = _buildBeadConfig();

        _render();

        document.addEventListener('input', _handleInput);
        document.addEventListener('focusin', _handleInputFocus);
        document.addEventListener('click', _handleIconButtonClick, true);
        window.addEventListener('resize', _onResize);

        if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
          window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
        }
      })
      .catch(function (err) {
        console.error('[PathBead] Init error:', err);
      });
  }

  document.addEventListener('DOMContentLoaded', function () { _init(); });
})();
